import styled from "@emotion/styled";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import type { DateGroup } from "src/state/NotificationsCubit/NotificationsCubit";
import NotificationsCubit from "src/state/NotificationsCubit/NotificationsCubit";
import createTrackEvent from "src/lib/createTrackEvent";
import translate from "src/lib/translate";
import { tracker } from "src/state/state";
import { AppQueryPopupsController } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import InDialog from "src/ui/components/InDialog/InDialog";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import {
  NotificationItemCard,
  DateGroupLabel
} from "src/ui/components/NotificationBell/NotificationCardShared";

const Wrapper = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const LoadMoreButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.85rem;
  margin-top: 0.5rem;
  background: var(--Secondary-Dark-Cream, #f2efe7);
  border-radius: 0.5rem;
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  text-align: center;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const EmptyState = styled.p`
  text-align: center;
  color: var(--color-charcoal-light, #999);
  padding: 2rem 0;
`;

const GroupSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const formatDateGroupLabel = (group: DateGroup): string => {
  if (group.labelKey === "today") {
    return translate("notifications.date.today");
  }
  if (group.labelKey === "yesterday") {
    return translate("notifications.date.yesterday");
  }
  return new Date(group.dateValue)
    .toLocaleDateString("en-US", { month: "short", day: "numeric" })
    .toUpperCase();
};

const NotificationsDialog: FC<{ returnUrl: string }> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();
  const [{ hasMoreRead, loading, loadingRead }, cubit] =
    useBlocNext(NotificationsCubit);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const groups = cubit.dateGroups;
  const empty = cubit.isEmpty;

  useEffect(() => {
    tracker.track(createTrackEvent("Notifications Opened"));
    void cubit.loadReadNotifications();
    void cubit.loadUnread();
  }, []);

  const onClose = useCallback(() => {
    void cubit.markAllAsRead();
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, { multiBack: true, replace: true });
  }, [cubit, navigate, returnUrl]);

  const handleToggle = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <InDialog
      title={translate("notifications.title")}
      onClose={onClose}
      width="calc(100vw - 8rem)"
      height="calc(100vh - 8rem)"
    >
      <Wrapper>
        <List role="list" aria-label={translate("notifications.title")}>
          {loading && groups.length === 0 && (
            <EmptyState>{translate("notifications.loading")}</EmptyState>
          )}

          {empty && !loadingRead && (
            <EmptyState>{translate("notifications.empty")}</EmptyState>
          )}

          {groups.map((group) => (
            <GroupSection key={group.dateValue} role="listitem">
              <DateGroupLabel>{formatDateGroupLabel(group)}</DateGroupLabel>
              {group.items.map((item) => (
                <NotificationItemCard
                  key={item.id}
                  item={item}
                  expanded={expandedId === item.id}
                  onToggle={() => handleToggle(item.id)}
                />
              ))}
            </GroupSection>
          ))}

          {hasMoreRead && (
            <LoadMoreButton
              onClick={() => void cubit.loadMoreRead()}
              disabled={loadingRead}
              aria-busy={loadingRead}
            >
              {loadingRead
                ? translate("notifications.loading")
                : translate("notifications.loadMore")}
            </LoadMoreButton>
          )}
        </List>
      </Wrapper>
    </InDialog>
  );
};

export default NotificationsDialog;
